/**
 * Bulk Import Schema - AI prompt for generating Tag Rules JSON by variant.
 * For Media Devoted | Created By Ciro Autiero
 */

import { BULK_MAX_RULES, LIST_METRIC_FIELDS } from './constants';
import type { TagRuleVariant } from '@/hooks';
import { HOUR_BULK_PROMPT_NOTE } from '@/features/hour-tags';

const FIELDS_LIST = LIST_METRIC_FIELDS.map(
  (f) => `  - "${f.key}" (${f.label})`,
).join('\n');

type VariantKey = TagRuleVariant | 'list';

const VARIANT_LABELS: Record<VariantKey, string> = {
  text: 'Text',
  gateway: 'Gateway',
  path: 'Path',
  lander: 'Lander',
  offer: 'Offer',
  list: 'List',
  phone: 'Phone',
  drop: 'Drop',
  source: 'Source',
  hour: 'Hour',
};

/** Variant-specific context: what this rule type tags and what to focus on. */
const VARIANT_CONTEXT: Record<VariantKey, string> = {
  text: 'TEXT tag rules label SMS creative content (message templates). Tags appear on texts in campaign stats. Focus on rules that identify high-performing copy, CTR, conversions per text.',
  gateway: 'GATEWAY tag rules label sender numbers. Tags appear on gateways in campaign stats. Focus on rules that identify best-performing senders, volume, deliverability per gateway.',
  path: 'PATH tag rules label URL paths. Tags appear on paths in campaign stats. Focus on rules that identify high-converting paths, traffic quality, ROI per path.',
  lander: 'LANDER tag rules label landing pages. Tags appear on landers in campaign stats. Focus on rules that identify top-performing landers, conversion rates, profit per lander.',
  offer: 'OFFER tag rules label offers. Tags appear on offers in campaign stats. Focus on rules that identify best offers, FTD rates, ROI per offer.',
  list: 'LIST tag rules label leads lists. Tags appear on lists in campaign stats. Focus on rules that identify high-quality lists, profit, conversions per list.',
  phone: 'PHONE tag rules label phone + source_id combinations. Tags appear on phones in campaign stats. Focus on rules that identify high-performing phones, profit, conversions per phone.',
  drop: 'DROP tag rules label individual SMS drops. Tags appear on drops in campaign stats. Focus on rules that identify high-performing drops, profit, conversions, ROI per drop.',
  source: 'SOURCE tag rules label traffic sources. Tags appear on sources in campaign stats. Focus on rules that identify high-quality sources, profit, conversions per source.',
  hour: "HOUR tag rules label the 24 hours of the day on the recipients' local clock, judged per campaign. The Drop Generator's hour rules read these tags. Focus on rules that identify golden hours (high EPC, ROI, FTD per visit) and dead hours (spend with no return).",
};

/** Variant-specific example rule names for the full example. */
const VARIANT_EXAMPLE_NAMES: Record<VariantKey, Record<string, string>> = {
  text: {
    today: 'Today Hot Text',
    weekly: 'Top Copy (7d)',
    elite30: 'Elite Text (30d)',
    mtd: 'MTD Best Text',
    lastMonth: 'Last Month Top Copy',
    ytd: 'YTD High CTR Text',
    lastYear: 'Last Year Elite Text',
  },
  gateway: {
    today: 'Today Best Sender',
    weekly: 'Weekly Top Gateway',
    elite30: 'Elite Sender (30d)',
    mtd: 'MTD Best Gateway',
    lastMonth: 'Last Month Top Sender',
    ytd: 'YTD High Volume Gateway',
    lastYear: 'Last Year Elite Gateway',
  },
  path: {
    today: 'Today Hot Path',
    weekly: 'Weekly Top Path',
    elite30: 'Elite Path (30d)',
    mtd: 'MTD Best Path',
    lastMonth: 'Last Month Top Path',
    ytd: 'YTD High Converting Path',
    lastYear: 'Last Year Elite Path',
  },
  lander: {
    today: 'Today Hot Lander',
    weekly: 'Weekly Top Lander',
    elite30: 'Elite Lander (30d)',
    mtd: 'MTD Best Lander',
    lastMonth: 'Last Month Top Lander',
    ytd: 'YTD High Converting Lander',
    lastYear: 'Last Year Elite Lander',
  },
  offer: {
    today: 'Today Hot Offer',
    weekly: 'Weekly Top Offer',
    elite30: 'Elite Offer (30d)',
    mtd: 'MTD Best Offer',
    lastMonth: 'Last Month Top Offer',
    ytd: 'YTD High FTD Offer',
    lastYear: 'Last Year Elite Offer',
  },
  list: {
    today: 'Today Hot List',
    weekly: 'Weekly Top List',
    elite30: 'Elite List (30d)',
    mtd: 'MTD Best List',
    lastMonth: 'Last Month Top List',
    ytd: 'YTD High ROI List',
    lastYear: 'Last Year Elite List',
  },
  phone: {
    today: 'Today Hot Phone',
    weekly: 'Weekly Top Phone',
    elite30: 'Elite Phone (30d)',
    mtd: 'MTD Best Phone',
    lastMonth: 'Last Month Top Phone',
    ytd: 'YTD High ROI Phone',
    lastYear: 'Last Year Elite Phone',
  },
  drop: {
    today: 'Today Hot Drop',
    weekly: 'Weekly Top Drop',
    elite30: 'Elite Drop (30d)',
    mtd: 'MTD Best Drop',
    lastMonth: 'Last Month Top Drop',
    ytd: 'YTD High ROI Drop',
    lastYear: 'Last Year Elite Drop',
  },
  hour: {
    today: 'Today Hot Hour',
    weekly: 'Golden Hour (7d)',
    elite30: 'Elite Hour (30d)',
    mtd: 'MTD Best Hour',
    lastMonth: 'Last Month Top Hour',
    ytd: 'YTD High ROI Hour',
    lastYear: 'Last Year Elite Hour',
  },
  source: {
    today: 'Today Hot Source',
    weekly: 'Weekly Top Source',
    elite30: 'Elite Source (30d)',
    mtd: 'MTD Best Source',
    lastMonth: 'Last Month Top Source',
    ytd: 'YTD High ROI Source',
    lastYear: 'Last Year Elite Source',
  },
};

function getVariantExample(variant: VariantKey): string {
  const n = VARIANT_EXAMPLE_NAMES[variant];
  return `{
  "rules": [
    {
      "name": "${n.today}",
      "color": "#dc2626",
      "groupKey": "recency-tier",
      "conditions": [{ "field": "profit", "operator": "gte", "value": 100 }],
      "dateRangePreset": "today"
    },
    {
      "name": "${n.weekly}",
      "color": "#22c55e",
      "groupKey": "recency-tier",
      "conditions": [
        { "field": "profit", "operator": "gte", "value": 500 },
        { "field": "conversions", "operator": "gte", "value": 10 }
      ],
      "dateRangePreset": "last-7-days"
    },
    {
      "name": "${n.elite30}",
      "color": "#16a34a",
      "groupKey": "profit-tier",
      "conditions": [
        { "field": "profit", "operator": "gte", "value": 2000 },
        { "field": "customConversions7", "operator": "gte", "value": 30 }
      ],
      "dateRangePreset": "last-30-days"
    },
    {
      "name": "${n.mtd}",
      "color": "#2563eb",
      "groupKey": "period-tier",
      "conditions": [{ "field": "profit", "operator": "gte", "value": 1000 }],
      "dateRangePreset": "this-month"
    },
    {
      "name": "${n.lastMonth}",
      "color": "#7c3aed",
      "groupKey": "period-tier",
      "conditions": [{ "field": "profit", "operator": "gte", "value": 3000 }],
      "dateRangePreset": "last-month"
    },
    {
      "name": "${n.ytd}",
      "color": "#0d9488",
      "groupKey": "period-tier",
      "conditions": [
        { "field": "profit", "operator": "gte", "value": 20000 },
        { "field": "conversions", "operator": "gte", "value": 100 }
      ],
      "dateRangePreset": "this-year"
    },
    {
      "name": "${n.lastYear}",
      "color": "#64748b",
      "groupKey": "period-tier",
      "conditions": [{ "field": "profit", "operator": "gte", "value": 50000 }],
      "dateRangePreset": "last-year"
    }
  ]
}`;
}

function getBasePrompt(variant: VariantKey): string {
  const label = VARIANT_LABELS[variant];
  const entity = label.toLowerCase() + 's'; // texts, gateways, paths, landers, offers, lists
  const context = VARIANT_CONTEXT[variant];

  return `You are a professional marketing analyst. Generate a JSON object for TrafficPulse ${label} Tag Rules bulk import.

**${label} rules:** ${context}

**Critical:** Do NOT default every rule to last-30-days. You must create a complete, professional rule set that handles every scenario. Choose the most appropriate date range for each rule:

- **Single day:** today, yesterday
- **Recent (hours/days):** last-24-hours, last-48-hours, last-3-days, last-7-days, last-14-days
- **Rolling:** last-30-days
- **Monthly:** this-month (month-to-date), last-month (full previous month)
- **Yearly:** this-year (year-to-date), last-year (full previous year)
- **Custom:** fromDate + toDate, or fromDate + toDateIsToday for MTD

Create rules that cover every scenario using the available fields. Mix date ranges: e.g. today, last-7-days, this-month, last-month, this-year, last-30-days.

## Format
Output a single JSON object: { "rules": [ ... ] }
**Limit:** Maximum ${BULK_MAX_RULES} rules per import.

---

## Rule types (how to structure rules)

**Prefer groups.** Use groupKey for most rules. Only omit groupKey for standalone flags that don't belong in a tier.

### 1. Rule with group (recommended)
Use groupKey for tiers. Rules with the same groupKey are mutually exclusive: only one matches per ${entity.slice(0, -1)} (first match wins). Examples: profit-tier, roi-tier, volume-tier, ftd-tier. Pick dateRangePreset to match the scenario.
\`\`\`json
{
  "name": "High Profit",
  "groupKey": "profit-tier",
  "conditions": [{ "field": "profit", "operator": "gte", "value": 500 }],
  "dateRangePreset": "last-7-days"
}
\`\`\`

### 2. Rule with group + AND conditions (2+ statements)
All conditions must pass. Use for combined criteria within a tier.
\`\`\`json
{
  "name": "Elite Profit",
  "groupKey": "profit-tier",
  "conditions": [
    { "field": "profit", "operator": "gte", "value": 2000 },
    { "field": "customConversions7", "operator": "gte", "value": 30 }
  ],
  "dateRangePreset": "last-30-days"
}
\`\`\`

### 3. Standalone rule (exception only)
Omit groupKey only when the tag is a one-off flag that doesn't compete with other rules. Use sparingly.
\`\`\`json
{
  "name": "MTD Profit Watch",
  "conditions": [{ "field": "profit", "operator": "gte", "value": 200 }],
  "dateRangePreset": "custom",
  "fromDate": "2026-03-01",
  "toDateIsToday": true
}
\`\`\`

---

## Rule order in JSON (critical for groups)

**Order matters.** Rules in the \`rules\` array are assigned \`orderIndex\` in array order: first rule = lowest index (checked last), last rule = highest index (checked first). Evaluation checks rules by **descending** orderIndex — so the **last** rule in the array is checked **first**.

- **Within a group:** Only **one** rule matches per ${entity.slice(0, -1)} (mutually exclusive). Put the **most specific** rule last, the **catch-all** first. Example for roi-tier: \`[Negative ROI, Weak ROI, Average ROI, Strong ROI, Exceptional ROI]\` — so Exceptional (ROI ≥ 200) is checked before Strong (≥ 100).
- **Across groups:** An ${entity.slice(0, -1)} can have **multiple** tags (e.g. "Elite Offer" from period-tier + "High FTD" from ftd-tier).

**Example — roi-tier (correct order):**
\`\`\`json
{
  "rules": [
    { "name": "Negative ROI",
      "color": "#dc2626",
      "groupKey": "roi-tier",
      "conditions": [{ "field": "roi", "operator": "lt", "value": 0 }],
      "dateRangePreset": "last-7-days" },
    { "name": "Weak ROI",
      "color": "#f59e0b",
      "groupKey": "roi-tier",
      "conditions": [
        { "field": "roi", "operator": "gte", "value": 0 },
        { "field": "roi", "operator": "lt", "value": 40 }
      ],
      "dateRangePreset": "last-7-days" },
    { "name": "Average ROI",
      "color": "#84cc16",
      "groupKey": "roi-tier",
      "conditions": [{ "field": "roi", "operator": "gte", "value": 40 }],
      "dateRangePreset": "last-7-days" },
    { "name": "Strong ROI",
      "color": "#22c55e",
      "groupKey": "roi-tier",
      "conditions": [{ "field": "roi", "operator": "gte", "value": 100 }],
      "dateRangePreset": "last-7-days" },
    { "name": "Exceptional ROI",
      "color": "#059669",
      "groupKey": "roi-tier",
      "conditions": [
        { "field": "roi", "operator": "gte", "value": 200 },
        { "field": "conversions", "operator": "gte", "value": 10 }
      ],
      "dateRangePreset": "last-7-days" }
  ]
}
\`\`\`
Order in array = evaluation order (last = checked first).

---

## Rule shape
- name (required, string, 1-80 chars): Tag label shown on ${entity}
- color (optional, string): Hex color e.g. "#22c55e"
- groupKey (recommended, string, max 80 chars): Rules with same groupKey are mutually exclusive; first match wins.
- conditions (required, array): All conditions must pass (AND logic). At least one condition.
- dateRangePreset (optional, string): Choose the best preset for each rule—see presets below. Do not default all rules to last-30-days.
- fromDate, toDate (optional, ISO YYYY-MM-DD): Required when dateRangePreset is "custom" and toDateIsToday is false
- toDateIsToday (optional, boolean, default false): When true with custom preset, end date is always today.
- enabled (optional, boolean, default true)

---

## Drop options context (for AI-optimized drops)
When drops use AI Optimization, they can be configured with:
- **aiOptimizationType**: ROI or CTR — optimize by return on investment or click-through rate
- **aiOptimizationBatchAmount**: number of recipients per batch
- **aiOptimizationMinimalValuePercent**: minimum % threshold; drop pauses if below
- **aiOptimizationSendUntil**: date/time to stop sending new batches (ISO format)

Other drop options: Include duplicates, Send in batches, New domains only. Consider ROI/CTR metrics when rules are used with AI-optimized drops.

---

## Condition shape
Each condition: { "field": "<metric>", "operator": "<op>", "value": <number> }
Optional **countryThresholds** (array, max 50): per-country overrides for that condition’s numeric threshold only. Countries not listed use **value** (default).
Example: default 100 everywhere, Australia 50:
\`{ "field": "conversions", "operator": "gte", "value": 100, "countryThresholds": [{ "countryKey": "australia", "value": 50 }] }\`
Duplicate countryKey in the same condition is invalid.
Optional **campaignThresholds** (array, max 50): per-campaign overrides, which BEAT countryThresholds for those campaigns. Each entry needs **provider** ("VOLUUM", "BINOM" or "TRES_COMMAS") and **campaignId** (the tracker campaign id) — the id alone is ambiguous: Voluum ids are UUIDs, Binom ids are small integers, and a Tres Commas id can equal a Voluum one.
Example: default 30, Netherlands 50, but one campaign in it 80:
\`{ "field": "customConversions6", "operator": "gte", "value": 30, "countryThresholds": [{ "countryKey": "netherlands", "value": 50 }], "campaignThresholds": [{ "provider": "VOLUUM", "campaignId": "6fbaa9fc-726e-4aa5-83fb-37ea9a9b9d30", "value": 80 }] }\`
Duplicate (provider, campaignId) in the same condition is invalid.

### Metrics (field keys) — use only these; pick fields that fit each rule
${FIELDS_LIST}

### Operators
- gte: greater than or equal (≥)
- lte: less than or equal (≤)
- gt: greater than (>)
- lt: less than (<)
- eq: equal (=)

---

## Date range presets (use the right one for each scenario)

| Preset | Description |
|--------|-------------|
| today | Single day: today only |
| yesterday | Single day: yesterday only |
| last-24-hours | Today |
| last-48-hours | Yesterday + today |
| last-3-days | 3 days ending today |
| last-7-days | 7 days ending today |
| this-month | Month-to-date: first day of current month → today |
| last-30-days | Rolling 30 days ending today |
| last-month | Full previous calendar month |
| this-year | Year-to-date: Jan 1 → today |
| last-year | Full previous calendar year |
| custom | Use fromDate + toDate, or fromDate + toDateIsToday for arbitrary ranges |

### Custom with toDateIsToday
When dateRangePreset is "custom" and toDateIsToday is true:
- Set fromDate (e.g. "2025-02-01")
- Set toDateIsToday: true
- Omit toDate

\`\`\`json
{
  "name": "MTD Performance",
  "conditions": [{ "field": "profit", "operator": "gte", "value": 100 }],
  "dateRangePreset": "custom",
  "fromDate": "2025-02-01",
  "toDateIsToday": true
}
\`\`\`

---

## Full example (complete — all date scenarios, tailored for ${label})
${getVariantExample(variant)}

Return only valid JSON, no markdown or explanation.`;
}

export function buildBulkImportPromptForVariant(
  variant: VariantKey,
  userContext?: string,
): string {
  const base =
    getBasePrompt(variant) + (variant === 'hour' ? HOUR_BULK_PROMPT_NOTE : '');
  if (!userContext?.trim()) return base;
  const trimmed = userContext.trim();
  return `## Your context / requirements
${trimmed}

---

${base}`;
}
